/**
 * Helpers d'affichage pour les imputations sur sous-projets "divers"
 * (sous-chantiers JLO / GP / WAG rattachés à un parent générique
 * "26E0259 - GP DIVERS TRAVAUX" ou similaire).
 *
 * Demande Jean-Luc OGER (mai 2026) : remplacer le préfixe entité
 * crochets `[GP] FIMOR` / `[JLO] FIMOR` par le **code du projet parent**
 * suivi du nom client, ex : `26E0259 - FIMOR`. Le numéro d'affaire est
 * utile à la facturation, le crochet entité ne l'était pas.
 *
 * Compatibilité legacy : les `time_entries.description` existantes en
 * DB peuvent contenir l'ancien format `[GP] FIMOR`. La fonction
 * `formatDiversLabel` parse ces deux formats et retourne toujours le
 * nouveau, à partir du `parent_code` et du nom client extrait.
 */

/**
 * Construit l'étiquette d'affichage pour une imputation divers.
 *
 * @param parentCode  Code du projet parent (ex: "26E0259")
 * @param clientName  Nom du sous-chantier (ex: "FIMOR")
 * @returns Étiquette formatée : `"26E0259 - FIMOR"`
 */
export function formatDiversLabel(parentCode: string, clientName: string): string {
  return `${parentCode} - ${clientName}`;
}

/**
 * Extrait le nom client d'une description legacy de time_entry divers.
 * Gère trois formats historiques :
 *   - `[GP] FIMOR`              → `"FIMOR"`
 *   - `[JLO] HOTEL H24`         → `"HOTEL H24"`
 *   - `26E0259 - FIMOR`         → `"FIMOR"` (nouveau format ré-extrait)
 *   - `FIMOR` (sans préfixe)    → `"FIMOR"` (commentaire utilisateur brut)
 *
 * Si aucune correspondance, retourne la string brute (pas de transformation).
 *
 * @param description Valeur de `time_entries.description`
 */
export function extractDiversClientName(description: string): string {
  if (!description) return '';
  // Format `[XX] CLIENT` ou `[XX-2025] CLIENT`
  const bracketMatch = description.match(/^\[[A-Z0-9-]+\]\s*(.+)$/);
  if (bracketMatch) return bracketMatch[1].trim();
  // Format `CODE - CLIENT` (nouveau)
  const codeMatch = description.match(/^[A-Z0-9-]+\s*-\s*(.+)$/i);
  if (codeMatch) return codeMatch[1].trim();
  return description.trim();
}

/**
 * Reconstitue une étiquette divers normalisée depuis un time_entry joint
 * à son project. Utilisée par AdminValidate et regeneratePdf pour afficher
 * cohéremment les anciennes ET les nouvelles entries.
 *
 * @param parentCode    Code du projet parent (`entry.project.code`)
 * @param description   `entry.description` brute
 * @returns `"PARENT_CODE - CLIENT"` ou la description brute si extraction impossible
 */
export function formatDiversEntryLabel(
  parentCode: string | undefined | null,
  description: string | undefined | null,
): string {
  if (!description) return parentCode ?? '';
  const clientName = extractDiversClientName(description);
  if (!parentCode) return clientName || description;
  return formatDiversLabel(parentCode, clientName);
}
